import axios from 'axios';
import { API_BASE_URL } from '../lib/constants.js';
import { getAccessToken, isMsalEnabled, getAccount, isInteractiveRedirectPending, forceMsalSignOut } from '../lib/auth.js';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token on every request.
// Priority: impersonation token → portal preview token → MSAL token.
// The MSAL token is never replaced — session overrides are applied here only.
api.interceptors.request.use(async (config) => {
  // For FormData, remove the instance-default Content-Type so the browser sets it with the multipart boundary
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }

  for (const key of ['impersonation', 'portalPreview', 'magicSession']) {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.token) {
          config.headers.Authorization = `Bearer ${parsed.token}`;
          return config;
        }
      }
    } catch { /* malformed sessionStorage entry — continue to next key */ }
  }
  const token = await getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Normalize error messages — handles both { error: "..." } and { errors: [...] }
// 401 redirects to /login (skipped if already there to prevent loops).
api.interceptors.response.use(
  (res) => res,
  (err) => {
    // PLATFORM-HARDENING-1 WS1: on 401 the session is invalid — PURGE it so the
    // auth gate can't keep re-rendering the app shell on a stale token (the
    // "fully rendered while Not signed in" exposure). Redirect to '/' (the gate's
    // natural sign-in), not '/login' (unhandled for email-auth tenants → loop).
    if (err.response?.status === 401) {
      let onAuthPage = false;
      try {
        const p = window.location.pathname;
        onAuthPage = p.startsWith('/auth/') || p === '/';
      } catch { /* ignore */ }
      try {
        sessionStorage.removeItem('magicSession');
        sessionStorage.removeItem('impersonation');
        sessionStorage.removeItem('__sweep_token');
      } catch { /* ignore */ }

      if (isMsalEnabled) {
        // AUTH-MSAL-REDIRECT-RACE-1 fix #2: the reload-to-'/' below was written for the
        // email-auth (magicSession) flow — for MSAL tenants it must never fire independently,
        // since it can race auth.js's own acquireTokenRedirect()/logoutRedirect() for control
        // of window.location. auth.js owns the interactive-redirect decision here instead.
        const account = getAccount();
        if (account) {
          if (!isInteractiveRedirectPending()) {
            // A token MSAL still considers present/valid just got 401'd by the backend —
            // force a real sign-out (fix #4) instead of leaving the stale account in place
            // to repeat the same failure.
            forceMsalSignOut();
          }
          return new Promise(() => {}); // navigation (ours or already in flight) owns this tab now
        }
        // No MSAL account at all — nothing to sign out from. UnauthenticatedTemplate already
        // renders SignInPage in this case; just let the rejection propagate normally below.
      } else if (!onAuthPage) {
        window.location.href = '/';
        return new Promise(() => {});
      }
    }
    const data = err.response?.data;
    const message = data?.error
      ?? (Array.isArray(data?.errors) ? data.errors.join(' ') : null)
      ?? err.message
      ?? 'Unknown error';
    // FDAV-PART11-REMEDIATION-1 REM-10: attach the backend's `code` (and status) to the thrown
    // Error so callers can branch on a specific denial (PIN_EXPIRED, SIGNER_PIN_LOCKED, etc.)
    // instead of string-matching err.message. Additive only — every existing caller that reads
    // only err.message is unaffected.
    // VAP-PO-GATE-1: same pattern, additive -- attach the full response body too, since a
    // caller may need a structured detail array (blockedPairs), not just a message/code.
    // Real bug caught live: a caller written as `e.response?.data` (the raw axios shape)
    // gets `response: undefined` here -- this interceptor replaces the rejection with a
    // plain Error, it never has a `.response` at all. Every VAP-PO-GATE-1 override attempt
    // silently fell through to the generic error path instead of the blocked-pairs UI.
    return Promise.reject(Object.assign(new Error(message), { code: data?.code, status: err.response?.status, data }));
  }
);

export default api;

/** Generic paginated GET. Returns { data, pagination }. */
export async function fetchPage(path, page = 1, limit = 20, params = {}) {
  const res = await api.get(path, { params: { page, limit, ...params } });
  return res.data;
}

// AUDIT-FIX-CRM-ROCHEM-DATA-1 Defect 3 (2026-08-22) — every picker that needs the FULL set (not a
// paginated table view) must page until pagination.total is satisfied, not trust a single capped
// fetch. Use this instead of a bare fetchPage(path, 1, N) call for any dropdown/picker use case.
// Guards against a runaway loop (a route that lies about pagination.total, or a total that never
// stops growing across pages) with a hard cap on total pages fetched.
const FETCH_ALL_MAX_PAGES = 50;

export async function fetchAllPages(path, limit = 200, params = {}) {
  const first = await fetchPage(path, 1, limit, params);
  const rows  = [...(first.data ?? [])];
  const total = first.pagination?.total ?? rows.length;
  const pages = first.pagination?.pages ?? Math.ceil(total / limit) ?? 1;

  for (let page = 2; page <= Math.min(pages, FETCH_ALL_MAX_PAGES); page++) {
    const next = await fetchPage(path, page, limit, params);
    rows.push(...(next.data ?? []));
  }

  if (pages > FETCH_ALL_MAX_PAGES) {
    console.warn(`fetchAllPages(${path}): stopped at ${FETCH_ALL_MAX_PAGES} pages, ${pages} total — result is incomplete.`);
  }

  return rows;
}

export async function fetchOne(path, id) {
  const res = await api.get(`${path}/${id}`);
  return res.data;
}

export async function createOne(path, body) {
  const res = await api.post(path, body);
  return res.data;
}

export async function updateOne(path, id, body) {
  const res = await api.patch(`${path}/${id}`, body);
  return res.data;
}

export async function deleteOne(path, id) {
  const res = await api.delete(`${path}/${id}`);
  return res.data;
}
